import { Switch } from '@/common/ui/switch'
import { APPLICATION_COPY } from '@/locales/en'
import { H3 } from '@/common/ui/h3'
import { H2 } from '@/common/ui/h2'
import { useEffect, useRef, useState } from 'react'
import { Select, SelectTrigger, SelectContent, SelectGroup, SelectItem } from '@/common/ui/select'
import { AddressDetails, TradeRegistryAddressType } from '@/common/types'
import { getAddressString } from '@/common/utils/utils'

type Props = {
    clientPostalAddress: AddressDetails
    adviserRegistrationAddress?: AddressDetails
    defaultAddressType?: TradeRegistryAddressType
    onAddressTypeChange?: (addressType: TradeRegistryAddressType) => void
}

export function AddAccountParticipantCommsStep({
    clientPostalAddress,
    adviserRegistrationAddress,
    defaultAddressType,
    onAddressTypeChange,
}: Props) {
    const copy = APPLICATION_COPY.participantComms

    // Determine default selection - client is required, adviser is optional
    const getDefaultAddressType = (): TradeRegistryAddressType => {
        if (defaultAddressType) return defaultAddressType
        if (adviserRegistrationAddress) return 'adviser'
        return 'client' // Client is required, so default to it
    }

    const [selectedAddressType, setSelectedAddressType] = useState<TradeRegistryAddressType>(() =>
        getDefaultAddressType(),
    )
    // Tracks whether an address type has already been locked in, either by the user or by
    // the auto-select below, so a later prop change never clobbers a real choice.
    const hasLockedAddressType = useRef(Boolean(defaultAddressType))

    // adviserRegistrationAddress loads asynchronously and can arrive after this step has
    // already rendered with the 'client' fallback. Switch to it once it shows up (unless a
    // choice is already locked in), so account creation uses the address actually shown
    // here instead of a fallback recomputed independently at submission time.
    useEffect(() => {
        if (hasLockedAddressType.current) return
        if (!adviserRegistrationAddress) return

        hasLockedAddressType.current = true
        setSelectedAddressType('adviser')
        onAddressTypeChange?.('adviser')
    }, [adviserRegistrationAddress, onAddressTypeChange])

    const handleAddressTypeChange = (value: TradeRegistryAddressType) => {
        hasLockedAddressType.current = true
        setSelectedAddressType(value)
        onAddressTypeChange?.(value)
    }

    return (
        <div className="w-full flex flex-col justify-start items-start gap-12">
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
                <H2 className="self-stretch text-brand-forest text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                    {copy.title}
                </H2>
                <div className="self-stretch text-black text-base font-normal font-['Inter'] leading-6">
                    {copy.description}
                </div>
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-6">
                    <H3 className="self-stretch text-content-primary text-2xl md:text-3xl !font-normal font-['Serious Sans'] leading-8">
                        {copy.byEmail}
                    </H3>

                    <div className="self-stretch inline-flex justify-start items-start gap-6">
                        <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                            <div className="text-content-primary text-base font-medium leading-6">
                                <span className="text-black font-medium">{copy.tradeConfirmationsLabel} </span>
                                <span className="text-content-secondary">(required)</span>
                            </div>
                            <div className="text-black text-base font-normal leading-6">
                                {copy.tradeConfirmationsDescription}
                            </div>
                        </div>

                        <div className="flex items-center">
                            <Switch id="trade-confirmations" checked={true} disabled />
                        </div>
                    </div>

                    <div className="self-stretch border-t border-border-divider" />
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-6">
                    <H3 className="self-stretch text-content-primary text-2xl md:text-3xl !font-normal font-['Serious Sans'] leading-8">
                        {copy.byPost}
                    </H3>

                    <div className="self-stretch inline-flex justify-start items-start gap-6">
                        <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                            <div className="text-content-primary text-base font-medium leading-6">
                                {copy.tradeRegistryAddressLabel}
                            </div>
                            <div className="text-black text-base font-normal leading-6">
                                {copy.tradeRegistryAddressDescription}
                            </div>
                        </div>

                        <div className="flex items-center">
                            <Select value={selectedAddressType} onValueChange={handleAddressTypeChange}>
                                <SelectTrigger
                                    id="trade-registry-address"
                                    className="w-[120px] md:w-[200px] rounded-full justify-end"
                                >
                                    <span className="px-4">
                                        {selectedAddressType === 'adviser' ? 'Adviser' : 'Client'}
                                    </span>
                                </SelectTrigger>
                                <SelectContent align="end">
                                    <SelectGroup>
                                        <SelectItem value="adviser" disabled={!adviserRegistrationAddress}>
                                            <div className="flex flex-col">
                                                <span className="font-medium">Adviser</span>
                                                <span className="text-sm text-content-secondary">
                                                    {adviserRegistrationAddress
                                                        ? getAddressString(adviserRegistrationAddress)
                                                        : 'Set in adviser account settings'}
                                                </span>
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="client">
                                            <div className="flex flex-col">
                                                <span className="font-medium">Client</span>
                                                <span className="text-sm text-content-secondary">
                                                    {getAddressString(clientPostalAddress)}
                                                </span>
                                            </div>
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="self-stretch border-t border-border-divider" />
                </div>

                <p className="text-content-tertiary text-base font-normal font-['Inter'] leading-6 m-0">
                    {copy.footer}
                </p>
            </div>
        </div>
    )
}
